import { useState, type FormEvent } from 'react';
import { trackEmailCapture } from '@/lib/utils/analytics';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function alreadyCaptured(context: string): boolean {
    try {
        return window.localStorage.getItem(`msf_email_capture_${context}`) !== null;
    } catch {
        return false;
    }
}

function rememberCapture(context: string): void {
    try {
        window.localStorage.setItem(`msf_email_capture_${context}`, '1');
    } catch {
        /* noop — capture still fires, just not deduped on repeat visits */
    }
}

/**
 * No-account email-capture widget for visitors not ready to sign up — fires a PostHog
 * event only, no backend/Firestore write (same pattern as CalculatorFeedback/BlogFeedback).
 * There is no automated follow-up: someone reviews `email_captured` events in PostHog
 * and reaches out manually.
 */
export default function EmailCapture({
    context,
    heading,
    subheading,
    cta = 'Notify me',
}: {
    context: string;
    heading: string;
    subheading?: string;
    cta?: string;
}) {
    const [email, setEmail] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [submitted, setSubmitted] = useState(() =>
        typeof window !== 'undefined' ? alreadyCaptured(context) : false
    );

    if (submitted) {
        return (
            <div className="not-prose mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                Thanks — we'll be in touch.
            </div>
        );
    }

    const submit = (e: FormEvent) => {
        e.preventDefault();
        if (!EMAIL_RE.test(email)) {
            setError('Enter a valid email address.');
            return;
        }
        trackEmailCapture(context, email);
        rememberCapture(context);
        setSubmitted(true);
    };

    return (
        <form onSubmit={submit} className="not-prose mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
            <p className="text-sm font-semibold text-gray-900 mb-1">{heading}</p>
            {subheading && <p className="text-sm text-gray-600 mb-3">{subheading}</p>}
            <div className="flex flex-col sm:flex-row gap-2">
                <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                        setEmail(e.target.value);
                        setError(null);
                    }}
                    placeholder="you@example.com"
                    aria-label="Email address"
                    className="flex-1 rounded-full border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:border-blue-600"
                />
                <button
                    type="submit"
                    className="rounded-full bg-primary px-5 py-2 text-sm font-medium text-white hover:bg-primary/90 transition-colors whitespace-nowrap"
                >
                    {cta}
                </button>
            </div>
            {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
        </form>
    );
}
